/* ========================================
   ENHANCED ENGRAVING PRODUCT MODULE v2.1
   Clean circular cropping with movable/resizable crop areas
   ======================================== */

/* ============================================
   PRODUCTION MODE CONFIGURATION
   ============================================ */
const ENGRAVING_PRODUCTION_MODE = false; // Set to false when testing/debugging

const engravingLogger = {
  dev: (...args) => {
    if (!ENGRAVING_PRODUCTION_MODE) {
      console.log(...args);
    }
  },
  error: (...args) => console.error(...args),
  warn: (...args) => console.warn(...args),
  info: (message) => {
    if (!ENGRAVING_PRODUCTION_MODE) {
      console.log(message);
    } else {
      // Only show user-friendly messages in production
      if (message.includes('✅') || message.includes('🎉') || message.includes('✓')) {
        console.log(message);
      }
    }
  }
};

/* ========================================
   FALLBACK BROWSER IMAGE STORAGE CLASS
   ======================================== */

class FallbackBrowserImageStorage {
  constructor() {
    this.dbName = 'EngravingTempStorage';
    this.dbVersion = 1;
    this.storeName = 'tempImages';
    this.db = null;
  }

  async init() {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, this.dbVersion);
      
      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        this.db = request.result;
        resolve();
      };
      
      request.onupgradeneeded = (event) => {
        const db = event.target.result;
        if (!db.objectStoreNames.contains(this.storeName)) {
          const store = db.createObjectStore(this.storeName, { keyPath: 'id' });
          store.createIndex('timestamp', 'timestamp', { unique: false });
        }
      };
    });
  }

  async storeImage(id, imageDataUrl, metadata = {}) {
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([this.storeName], 'readwrite');
      const store = transaction.objectStore(this.storeName);
      
      const imageData = {
        id: id,
        dataUrl: imageDataUrl,
        metadata: metadata,
        timestamp: Date.now()
      };
      
      const request = store.put(imageData);
      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve(imageData);
    });
  }

  async getImage(id) {
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([this.storeName], 'readonly');
      const store = transaction.objectStore(this.storeName);
      const request = store.get(id);
      
      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve(request.result);
    });
  }

  async deleteImage(id) {
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([this.storeName], 'readwrite');
      const store = transaction.objectStore(this.storeName);
      const request = store.delete(id);
      
      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve();
    });
  }

  async clearOldImages(maxAge = 24 * 60 * 60 * 1000) {
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([this.storeName], 'readwrite');
      const store = transaction.objectStore(this.storeName);
      const index = store.index('timestamp');
      
      const cutoff = Date.now() - maxAge;
      const range = IDBKeyRange.upperBound(cutoff);
      
      const request = index.openCursor(range);
      request.onsuccess = (event) => {
        const cursor = event.target.result;
        if (cursor) {
          cursor.delete();
          cursor.continue();
        } else {
          resolve();
        }
      };
      request.onerror = () => reject(request.error);
    });
  }
}

/* ========================================
   MAIN ENGRAVING PRODUCT MODULE CLASS
   ======================================== */

class EngravingProductModule {
  constructor(container) {
    engravingLogger.dev('🎨 Initializing Enhanced Engraving Module v2.1');
    
    // Core properties
    this.container = container;
    container.__epmInstance = this; // expose for the variant-UI controller (live monochrome re-tint)
    this.productId = container.dataset.productId;
    this.sectionId = container.dataset.sectionId || 'default';
    this.uniqueId = this.sectionId;
    
    if (!this.productId) {
      throw new Error('Product ID is required for engraving module');
    }
    
    // Core data structures
    this.selectedVariants = new Map();
    this.uploadedFile = null;
    this.croppedImageData = null;
    this.isGrayscale = false;
    
    // Cropping variables
    this.canvas = null;
    this.ctx = null;
    this.image = null;
    this.scale = 1;
    this.offsetX = 0;
    this.offsetY = 0;
    this.isDragging = false;
    
    // Crop area variables
    this.cropX = 0;
    this.cropY = 0;
    this.cropWidth = 0;
    this.cropHeight = 0;
    
    // Image positioning variables
    this.imageX = 0;
    this.imageY = 0;
    this.imageWidth = 0;
    this.imageHeight = 0;
    
    // Zoom variables
    this.zoomFactor = 1.0;
    this.minZoom = 0.2;
    this.maxZoom = 3.0;
    this.baseImageWidth = 0;
    this.baseImageHeight = 0;
    this.baseImageX = 0;
    this.baseImageY = 0;

    // Enhanced functionality variables
    this.processingQueue = new Set();
    this.allQuantityInputs = Array.from(container.querySelectorAll('.qty-input'));
    
    // Mobile-specific variables
    this.lastKnownWidth = null;
    this.lastKnownHeight = null;
    this.isInputFocused = false;
    this.isResizing = false;
    
    // Security and rate limiting
    this.uploadAttempts = new Map();
    this.maxUploadAttempts = 20;
    this.rateLimitWindow = 60000;
    
    // Performance optimizations
    this.throttledResize = this.throttle(this.handleResize.bind(this), this.isMobileDevice() ? 500 : 100);
    this.debouncedValidation = this.debounce(this.validateInputs.bind(this), 500);
    this.debouncedUpdateVariant = this.debounce((variantId, quantity) => {
      this.updateVariantSelection(variantId, quantity);
    }, 300);
    
    // Memory management
    this.eventListeners = new Map();
    this.intersectionObserver = null;
    
    // Performance monitor
    this.performanceMonitor = window.PerformanceMonitor ? new window.PerformanceMonitor() : { 
      track: () => {}, mark: () => {}, measure: () => {}, error: () => {} 
    };
    
    // Browser storage
    this.browserStorage = window.BrowserImageStorage ? 
      new window.BrowserImageStorage() : 
      new FallbackBrowserImageStorage();
    this.browserStorageAvailable = true;
    
    // Feature detection
    this.features = {
      webgl: this.detectWebGL(),
      touch: 'ontouchstart' in window,
      fileAPI: window.File && window.FileReader && window.FileList,
      canvas: !!document.createElement('canvas').getContext,
      intersectionObserver: 'IntersectionObserver' in window,
      requestIdleCallback: 'requestIdleCallback' in window
    };
    
    // Settings from theme/global
    this.settings = this.loadSettings();
    
    // Error boundary state
    this.hasError = false;
    this.retryCount = 0;
    this.maxRetries = 3;
    
    // Initialize with error handling
    this.safeInit();
  }

  /* ========================================
     INITIALIZATION & SETTINGS
     ======================================== */

  async safeInit() {
    try {
      await this.initBrowserStorage();
      this.init();
      this.container.classList.add('loaded');
      this.performanceMonitor.mark('module_initialized');
      engravingLogger.dev('✅ Engraving module initialized successfully');
    } catch (error) {
      this.handleCriticalError('Initialization failed', error);
    }
  }

  async initBrowserStorage() {
    try {
      await this.browserStorage.init();
      await this.browserStorage.clearOldImages();
    } catch (error) {
      this.browserStorageAvailable = false;
      console.warn('Browser storage not available:', error);
    }
  }

  init() {
    this.createErrorBoundary();
    this.bindEvents();
    this.initQuantityDebouncing();
    this.initKeyboardNavigation();
    this.initMobileOptimizations();
    this.createLiveRegion();
    this.setupIntersectionObserver();
    this.initializeProgressTracking();
    this.updateUI();
    
    this.addEventListener(window, 'resize', this.throttledResize);
    this.addEventListener(document, 'visibilitychange', this.handleVisibilityChange.bind(this));
    
    this.setupAutoSave();
    this.loadState();
  }

  loadSettings() {
    const globalSettings = window.engravingSettings || {};
    
    const dataSettings = {
      maxFileSize: parseInt(this.container.dataset.maxFileSize) || 5,
      cropRatio: this.container.dataset.cropRatio || '1:1',
      cropShape: this.container.dataset.cropShape || 'rectangular',
      currency: this.container.dataset.currency || 'USD'
    };
    
    let jsonSettings = {};
    try {
      const jsonScript = document.querySelector(`[data-engraving-product="${this.uniqueId}"]`);
      if (jsonScript) {
        jsonSettings = JSON.parse(jsonScript.textContent);
      }
    } catch (error) {
      console.warn('Failed to load JSON settings:', error);
    }
    
    return {
      ...globalSettings,
      ...dataSettings,
      ...jsonSettings.settings,
      productData: jsonSettings
    };
  }

  isCircularMode() {
    return this.settings.cropShape === 'circular' || 
           window.engravingSettings?.isCircular === true ||
           this.container.dataset.cropShape === 'circular';
  }

  getAspectRatio() {
    const ratio = this.settings.cropRatio || '1:1';
    const [width, height] = ratio.split(':').map(Number);
    return width / height;
  }

  isMobileDevice() {
    return /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) || 
           window.innerWidth <= 989;
  }

  isTouchDevice() {
    return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  }

  detectWebGL() {
    try {
      const canvas = document.createElement('canvas');
      return !!(window.WebGLRenderingContext && 
        (canvas.getContext('webgl') || canvas.getContext('experimental-webgl')));
    } catch (e) {
      return false;
    }
  }

  /* ========================================
     EVENT BINDING
     ======================================== */

  bindEvents() {
    engravingLogger.dev('🔗 Binding events...');
    
    this.addEventListener(this.container, 'click', (e) => {
      this.safeExecute(() => {
        if (e.target.classList.contains('qty-btn')) {
          this.handleQuantityChange(e.target);
        }
      }, 'Quantity button click');
    });

    const fileInput = this.container.querySelector(`#engraving-file-${this.uniqueId}`);
    if (fileInput) {
      this.addEventListener(fileInput, 'change', (e) => {
        this.safeExecute(() => {
          this.handleFileUpload(e);
        }, 'File upload');
      });
      
      this.setupDragAndDrop(fileInput);
    }

    // Image control buttons
    const grayscaleBtn = this.container.querySelector(`#grayscale-toggle-${this.uniqueId}`);
    if (grayscaleBtn) {
      this.addEventListener(grayscaleBtn, 'click', () => {
        this.safeExecute(() => {
          this.toggleGrayscale();
        }, 'Grayscale toggle');
      });
    }

    const fitBtn = this.container.querySelector(`#fit-crop-${this.uniqueId}`);
    if (fitBtn) {
      this.addEventListener(fitBtn, 'click', () => {
        this.safeExecute(() => {
          this.fitToArea();
        }, 'Fit to area');
      });
    }

    const centerBtn = this.container.querySelector(`#center-image-${this.uniqueId}`);
    if (centerBtn) {
      this.addEventListener(centerBtn, 'click', () => {
        this.safeExecute(() => {
          this.centerImage();
        }, 'Center image');
      });
    }

    const rotateBtn = this.container.querySelector(`#rotate-image-${this.uniqueId}`);
    if (rotateBtn) {
      this.addEventListener(rotateBtn, 'click', () => {
        this.safeExecute(() => {
          this.rotateImage();
        }, 'Rotate image');
      });
    }

    const resetBtn = this.container.querySelector(`#reset-crop-${this.uniqueId}`);
    if (resetBtn) {
      this.addEventListener(resetBtn, 'click', () => {
        this.safeExecute(() => {
          this.resetCrop();
        }, 'Crop reset');
      });
    }

    const removeBtn = this.container.querySelector(`#remove-image-${this.uniqueId}`);
    if (removeBtn) {
      this.addEventListener(removeBtn, 'click', () => {
        this.safeExecute(() => {
          this.removeImage();
        }, 'Image removal');
      });
    }

    const addToCartBtn = this.container.querySelector('.add-to-cart-btn');
    if (addToCartBtn) {
      this.addEventListener(addToCartBtn, 'click', async () => {
        await this.safeAsync(() => this.addToCart(), 'Add to cart');
      });
    }

    this.setupCanvasEvents();
  }

  setupDragAndDrop(fileInput) {
    const uploadLabel = fileInput.closest('.upload-input-container').querySelector('.upload-label');
    if (!uploadLabel) return;

    let dragCounter = 0;

    this.addEventListener(uploadLabel, 'dragenter', (e) => {
      e.preventDefault();
      dragCounter++;
      uploadLabel.classList.add('drag-over');
    });

    this.addEventListener(uploadLabel, 'dragover', (e) => {
      e.preventDefault();
    });

    this.addEventListener(uploadLabel, 'dragleave', (e) => {
      e.preventDefault();
      dragCounter--;
      if (dragCounter === 0) {
        uploadLabel.classList.remove('drag-over');
      }
    });

    this.addEventListener(uploadLabel, 'drop', (e) => {
      e.preventDefault();
      dragCounter = 0;
      uploadLabel.classList.remove('drag-over');
      
      const files = e.dataTransfer.files;
      if (files.length > 0) {
        fileInput.files = files;
        this.handleFileUpload({ target: fileInput });
      }
    });
  }

  /* ========================================
     FILE VALIDATION & SECURITY
     ======================================== */

  async validateFile(file) {
    if (!this.checkUploadRateLimit()) {
      return {
        isValid: false,
        message: '⚠ Please wait a moment before uploading again.'
      };
    }

    const basicValidation = this.basicFileValidation(file);
    if (!basicValidation.isValid) return basicValidation;
    
    try {
      this.showProcessingIndicator('Processing image...');
      const securityChecks = await this.runBackgroundSecurityChecks(file);
      this.hideProcessingIndicator();
      
      if (!securityChecks.passed) {
        this.logSecurityEvent('security_check_failed', { 
          fileName: file.name, 
          reason: securityChecks.reason 
        });
        return {
          isValid: false,
          message: securityChecks.userMessage
        };
      }
      
      this.logSecurityEvent('file_validation_success', { 
        fileName: file.name, 
        type: file.type, 
        size: file.size 
      });
      
      return { isValid: true };
      
    } catch (error) {
      this.hideProcessingIndicator();
      this.logSecurityEvent('validation_error', { fileName: file.name, error: error.message });
      return { isValid: true };
    }
  }

  basicFileValidation(file) {
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png'];
    const maxSizeMB = this.settings.maxFileSize || 5;
    const maxSize = maxSizeMB * 1024 * 1024;

    if (!validTypes.includes(file.type)) {
      return {
        isValid: false,
        message: '⚠ Please upload a JPG, JPEG, or PNG file only.'
      };
    }

    if (file.size > maxSize) {
      return {
        isValid: false,
        message: `⚠ File size must be less than ${maxSizeMB}MB. Current size: ${(file.size / 1024 / 1024).toFixed(1)}MB`
      };
    }

    if (file.size === 0) {
      return {
        isValid: false,
        message: '⚠ File appears to be empty. Please select a different file.'
      };
    }

    if (file.name.length > 255) {
      return {
        isValid: false,
        message: '⚠ File name is too long. Please rename the file and try again.'
      };
    }

    return { isValid: true };
  }

  async runBackgroundSecurityChecks(file) {
    try {
      const signatureCheck = await this.relaxedFileSignatureCheck(file);
      if (!signatureCheck) {
        return {
          passed: false,
          reason: 'invalid_signature',
          userMessage: '⚠ This file doesn\'t appear to be a valid image. Please try a different file.'
        };
      }
      
      const integrityCheck = await this.basicImageIntegrityCheck(file);
      if (!integrityCheck) {
        return {
          passed: false,
          reason: 'corrupted_image',
          userMessage: '⚠ This image file appears to be corrupted. Please try a different file.'
        };
      }
      
      return { passed: true };
      
    } catch (error) {
      return { passed: true };
    }
  }

  async relaxedFileSignatureCheck(file) {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const bytes = new Uint8Array(e.target.result);
          
          if (file.type.includes('jpeg')) {
            resolve(bytes[0] === 0xFF && bytes[1] === 0xD8);
          } else if (file.type.includes('png')) {
            resolve(bytes[0] === 0x89 && bytes[1] === 0x50 && 
                   bytes[2] === 0x4E && bytes[3] === 0x47);
          } else {
            resolve(true);
          }
        } catch (error) {
          resolve(true);
        }
      };
      
      reader.onerror = () => resolve(true);
      reader.readAsArrayBuffer(file.slice(0, 10));
    });
  }

  async basicImageIntegrityCheck(file) {
    return new Promise((resolve) => {
      const img = new Image();
      
      const timeout = setTimeout(() => {
        resolve(true);
      }, 5000);

      img.onload = () => {
        clearTimeout(timeout);
        
        if (img.width > 0 && img.height > 0 && 
            img.width <= 20000 && img.height <= 20000) {
          resolve(true);
        } else {
          resolve(false);
        }
        
        URL.revokeObjectURL(img.src);
      };
      
      img.onerror = () => {
        clearTimeout(timeout);
        resolve(false);
      };
      
      img.src = URL.createObjectURL(file);
    });
  }

  checkUploadRateLimit() {
    const now = Date.now();
    const clientId = this.getClientId();
    
    this.cleanOldUploadAttempts(now);
    
    const attempts = this.uploadAttempts.get(clientId) || [];
    
    if (attempts.length >= this.maxUploadAttempts) {
      return false;
    }
    
    attempts.push(now);
    this.uploadAttempts.set(clientId, attempts);
    
    return true;
  }

  cleanOldUploadAttempts(now) {
    this.uploadAttempts.forEach((attempts, clientId) => {
      const validAttempts = attempts.filter(timestamp => 
        now - timestamp < this.rateLimitWindow
      );
      
      if (validAttempts.length === 0) {
        this.uploadAttempts.delete(clientId);
      } else {
        this.uploadAttempts.set(clientId, validAttempts);
      }
    });
  }

  getClientId() {
    if (!sessionStorage.getItem('engraving_client_id')) {
      sessionStorage.setItem('engraving_client_id', 
        Date.now().toString(36) + Math.random().toString(36).substr(2)
      );
    }
    return sessionStorage.getItem('engraving_client_id');
  }

  logSecurityEvent(eventType, details) {
    const securityLog = {
      timestamp: new Date().toISOString(),
      eventType,
      details,
      clientId: this.getClientId()
    };

    this.performanceMonitor.track('security_event', securityLog);
  }

  /* ========================================
     FILE UPLOAD HANDLING
     ======================================== */

  async handleFileUpload(event) {
    return this.measureAsyncPerformance('file_upload_handling', async () => {
      const file = event.target.files[0];
      if (!file) return;

      engravingLogger.dev(`📁 Processing file: ${file.name} (${(file.size / 1024 / 1024).toFixed(1)}MB)`);

      if (file.size > 1024 * 1024) {
        this.showFileProcessingProgress();
      }

      const validationResult = await this.validateFile(file);
      if (!validationResult.isValid) {
        this.showMessage(validationResult.message, 'warning');
        event.target.value = '';
        this.hideFileProcessingProgress();
        return;
      }

      this.uploadedFile = file;
      
      try {
        await this.loadImageForCropping(file);
        this.showMessage('✓ Image uploaded successfully!', 'success');
        this.updateLiveRegions('Image uploaded and ready for cropping');
      } catch (error) {
        this.showMessage(`⚠ Failed to process image: ${error.message}`, 'error');
        this.uploadedFile = null;
        event.target.value = '';
      } finally {
        this.hideFileProcessingProgress();
      }
    });
  }

  async loadImageForCropping(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = (e) => {
        const img = new Image();
        
        img.onload = () => {
          engravingLogger.dev(`🖼️ Image loaded: ${img.width}×${img.height}px`);
          this.image = img;
          this.setupCropper();
          this.showImagePreview();
          resolve();
        };
        
        img.onerror = () => {
          reject(new Error('Failed to load image. The file may be corrupted.'));
        };
        
        img.src = e.target.result;
      };
      
      reader.onerror = () => {
        reject(new Error('Failed to read file. Please try again.'));
      };
      
      reader.readAsDataURL(file);
    });
  }

  /* ========================================
     CANVAS SETUP - ALWAYS RECTANGULAR CANVAS
     ======================================== */

  setupCropper() {
    this.canvas = this.container.querySelector(`#image-cropper-${this.uniqueId}`);
    if (!this.canvas) {
      throw new Error('Canvas element not found');
    }
    
    this.ctx = this.canvas.getContext('2d');
    if (!this.ctx) {
      throw new Error('Failed to get canvas context');
    }

    if (this.canvas.width && this.canvas.height) {
      this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
    }

    requestAnimationFrame(() => {
      this.setupCanvasSize();
    });
  }

  setupCanvasSize() {
    if (this.isInputFocused || this.isResizing) {
      return;
    }
    
    this.isResizing = true;
    engravingLogger.dev(`📐 Setting up canvas size (${this.isCircularMode() ? 'circular' : 'rectangular'} mode)`);
    
    const isMobile = this.isMobileDevice();
    
    // ALWAYS use rectangular canvas setup
    if (isMobile) {
      this.setupMobileCanvasSize();
    } else {
      this.setupDesktopCanvasSize();
    }

    this.ctx.imageSmoothingEnabled = true;
    this.ctx.imageSmoothingQuality = 'high';

    this.displayWidth = this.canvas.width;
    this.displayHeight = this.canvas.height;

    if (!this.zoomFactor || this.zoomFactor === 1) {
      this.fitImageToCanvas();
      this.initializeCropArea();
    } else {
      this.updateImageDimensions();
    }
    
    this.drawImage();
    
    setTimeout(() => {
      this.isResizing = false;
    }, 100);
  }

  setupMobileCanvasSize() {
    const containerWidth = this.container.getBoundingClientRect().width;
    const displayWidth = Math.min(containerWidth - 40, 400);
    const aspectRatio = this.getAspectRatio();
    const displayHeight = Math.min(displayWidth / aspectRatio, 400);
    
    this.canvas.style.width = displayWidth + 'px';
    this.canvas.style.height = displayHeight + 'px';
    this.canvas.width = displayWidth;
    this.canvas.height = displayHeight;
    this.canvas.style.touchAction = 'none';
  }

  setupDesktopCanvasSize() {
    const wrapper = this.canvas.closest('.image-cropper-wrapper');
    const availableWidth = wrapper ? wrapper.getBoundingClientRect().width - 40 : 600;
    
    const aspectRatio = this.getAspectRatio();
    const displayWidth = Math.min(Math.max(availableWidth, 500), 800);
    const displayHeight = Math.min(displayWidth / aspectRatio, 600);
    
    this.canvas.style.width = displayWidth + 'px';
    this.canvas.style.height = displayHeight + 'px';
    this.canvas.width = displayWidth;
    this.canvas.height = displayHeight;
  }

  fitImageToCanvas() {
    if (!this.image) return;

    engravingLogger.dev('🎯 Fitting image to canvas...');
    
    const padding = 20;
    const availableWidth = this.canvas.width - (padding * 2);
    const availableHeight = this.canvas.height - (padding * 2);

    const scaleX = availableWidth / this.image.width;
    const scaleY = availableHeight / this.image.height;
    this.scale = Math.min(scaleX, scaleY);

    // Calculate base image dimensions and position
    this.baseImageWidth = this.image.width * this.scale;
    this.baseImageHeight = this.image.height * this.scale;
    this.baseImageX = (this.canvas.width - this.baseImageWidth) / 2;
    this.baseImageY = (this.canvas.height - this.baseImageHeight) / 2;

    // Reset zoom and update actual image dimensions
    this.zoomFactor = 1.0;
    
    // Set initial image position to center
    this.imageWidth = this.baseImageWidth;
    this.imageHeight = this.baseImageHeight;
    this.imageX = this.baseImageX;
    this.imageY = this.baseImageY;
    
    this.updateZoomIndicator();
    
    engravingLogger.dev(`📐 Image fitted: ${this.imageWidth}x${this.imageHeight} at (${this.imageX}, ${this.imageY})`);
  }

  updateImageDimensions() {
    if (!this.image) return;
    
    // Calculate new dimensions based on zoom
    const newWidth = this.baseImageWidth * this.zoomFactor;
    const newHeight = this.baseImageHeight * this.zoomFactor;
    
    // If this is the first time setting dimensions, center the image
    if (!this.imageWidth || !this.imageHeight) {
      this.imageWidth = newWidth;
      this.imageHeight = newHeight;
      this.imageX = this.baseImageX + (this.baseImageWidth - this.imageWidth) / 2;
      this.imageY = this.baseImageY + (this.baseImageHeight - this.imageHeight) / 2;
      return;
    }
    
    // For zoom operations, preserve the center point of the image
    const oldCenterX = this.imageX + this.imageWidth / 2;
    const oldCenterY = this.imageY + this.imageHeight / 2;
    
    this.imageWidth = newWidth;
    this.imageHeight = newHeight;
    
    // Keep the same center point after zoom
    this.imageX = oldCenterX - this.imageWidth / 2;
    this.imageY = oldCenterY - this.imageHeight / 2;
  }

  // Initialize crop area - for circular, make it square but movable/resizable
  initializeCropArea() {
  if (!this.image) return;
  
  engravingLogger.dev('🔲 Initializing crop area...');
  
  const isCircular = this.isCircularMode();
  
  if (isCircular) {
    // For circular mode, start the crop as the largest square that fits the
    // fitted image — i.e. pulled out to the image border at 100% zoom.
    const cropSize = Math.min(this.imageWidth, this.imageHeight);
    this.cropWidth = cropSize;
    this.cropHeight = cropSize; // Always square for circular

    // Center the crop area on the image
    this.cropX = this.imageX + (this.imageWidth - this.cropWidth) / 2;
    this.cropY = this.imageY + (this.imageHeight - this.cropHeight) / 2;

  } else {
    // Rectangular mode — start the crop matching the fitted image border at
    // 100% zoom (as large as the aspect ratio allows inside the image).
    const aspectRatio = this.getAspectRatio();

    if (this.imageWidth / this.imageHeight >= aspectRatio) {
      // Image is wider than the crop ratio → height-limited
      this.cropHeight = this.imageHeight;
      this.cropWidth = this.cropHeight * aspectRatio;
    } else {
      // Image is taller/narrower than the crop ratio → width-limited
      this.cropWidth = this.imageWidth;
      this.cropHeight = this.cropWidth / aspectRatio;
    }

    // Center the crop area on the image
    this.cropX = this.imageX + (this.imageWidth - this.cropWidth) / 2;
    this.cropY = this.imageY + (this.imageHeight - this.cropHeight) / 2;
  }
  
  // Ensure crop area stays within canvas bounds
  this.cropX = Math.max(0, Math.min(this.cropX, this.canvas.width - this.cropWidth));
  this.cropY = Math.max(0, Math.min(this.cropY, this.canvas.height - this.cropHeight));
  
  engravingLogger.dev(`🔲 Crop area initialized: ${this.cropWidth}x${this.cropHeight} at (${this.cropX}, ${this.cropY})`);
}

  /* ========================================
     CANVAS DRAWING - CLEAN VERSION
     ======================================== */

  drawImage() {
    if (!this.image || !this.ctx) return;

    this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
    this.drawCheckerPattern();

    if (this.isGrayscale) {
      // MONOCHROME preview = a duotone gradient map. Draw the design as
      // grayscale, then 'screen'-blend the variant colour over it. Screen math:
      //   result = C + L·(1 − C)
      // so a fully-black pixel (luminance L=0) becomes 100% the variant colour C,
      // fading linearly up to white (L=1). The only way black stays black is when
      // the variant colour itself is black. Preview only — the engraved file
      // stays true grayscale (see getCroppedImageData).
      this.ctx.filter = 'grayscale(100%)';
      this.ctx.drawImage(this.image, this.imageX, this.imageY, this.imageWidth, this.imageHeight);
      this.ctx.filter = 'none';

      this.ctx.save();
      this.ctx.globalCompositeOperation = 'screen';
      this.ctx.fillStyle = this.getActiveVariantTint();
      this.ctx.fillRect(this.imageX, this.imageY, this.imageWidth, this.imageHeight);
      this.ctx.restore();
    } else {
      this.ctx.filter = 'none';
      this.ctx.drawImage(this.image, this.imageX, this.imageY, this.imageWidth, this.imageHeight);
    }

    this.ctx.filter = 'none';
    this.drawCropOverlay();
    this.drawResizeHandles();
  }

  // Hex colour of the currently selected variant pill (default black = grayscale).
  getActiveVariantTint() {
    try {
      const pill = this.container.querySelector('[data-epm-vpill].is-active')
                 || this.container.querySelector('[data-epm-vpill]');
      const c = pill && pill.dataset ? pill.dataset.epmColor : '';
      if (c && /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(c)) return c;
    } catch (e) { /* fall through to default */ }
    return '#000000';
  }

  drawCheckerPattern() {
    const tileSize = 20;
    this.ctx.fillStyle = '#f0f0f0';
    this.ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);

    this.ctx.fillStyle = '#e0e0e0';
    for (let x = 0; x < this.canvas.width; x += tileSize) {
      for (let y = 0; y < this.canvas.height; y += tileSize) {
        if ((x / tileSize + y / tileSize) % 2 === 0) {
          this.ctx.fillRect(x, y, tileSize, tileSize);
        }
      }
    }
  }

  // FIXED: Draw crop overlay - clean version without extra darkening
  drawCropOverlay() {
    const isCircular = this.isCircularMode();
    
    this.ctx.save();
    this.ctx.fillStyle = 'rgba(0, 0, 0, 0.5)';
    
    if (isCircular) {
      // For circular mode, draw dark areas AROUND the circle, not over it
      const centerX = this.cropX + this.cropWidth / 2;
      const centerY = this.cropY + this.cropHeight / 2;
      const radius = Math.min(this.cropWidth, this.cropHeight) / 2;
      
      // Draw dark overlay everywhere EXCEPT the circular area
      this.ctx.beginPath();
      // Outer rectangle (entire canvas)
      this.ctx.rect(0, 0, this.canvas.width, this.canvas.height);
      // Inner circle (hole) - draw counter-clockwise to create hole
      this.ctx.arc(centerX, centerY, radius, 0, Math.PI * 2, true);
      this.ctx.fill('evenodd'); // Use even-odd rule to create hole
      
      // Draw clean circular border (no background, just the line)
      this.ctx.strokeStyle = '#ffffff';
      this.ctx.lineWidth = 2;
      this.ctx.setLineDash([6, 3]);
      this.ctx.beginPath();
      this.ctx.arc(centerX, centerY, radius, 0, Math.PI * 2);
      this.ctx.stroke();
      this.ctx.setLineDash([]);
      
    } else {
      // Rectangular mode - standard overlay (unchanged)
      if (this.cropY > 0) {
        this.ctx.fillRect(0, 0, this.canvas.width, this.cropY);
      }
      
      if (this.cropY + this.cropHeight < this.canvas.height) {
        this.ctx.fillRect(
          0, 
          this.cropY + this.cropHeight, 
          this.canvas.width, 
          this.canvas.height - (this.cropY + this.cropHeight)
        );
      }
      
      if (this.cropX > 0) {
        this.ctx.fillRect(0, this.cropY, this.cropX, this.cropHeight);
      }
      
      if (this.cropX + this.cropWidth < this.canvas.width) {
        this.ctx.fillRect(
          this.cropX + this.cropWidth, 
          this.cropY, 
          this.canvas.width - (this.cropX + this.cropWidth), 
          this.cropHeight
        );
      }
      
      // Draw rectangular border
      this.ctx.strokeStyle = '#ffffff';
      this.ctx.lineWidth = 2;
      this.ctx.setLineDash([6, 3]);
      this.ctx.strokeRect(this.cropX, this.cropY, this.cropWidth, this.cropHeight);
      this.ctx.setLineDash([]);
      
      this.drawCornerMarkers();
    }
    
    this.ctx.restore();
  }

  drawCornerMarkers() {
    const markerSize = 20;
    const markerThickness = 3;
    const markerColor = '#ffffff';
    
    this.ctx.strokeStyle = markerColor;
    this.ctx.lineWidth = markerThickness;
    this.ctx.setLineDash([]);
    
    const corners = [
      { x: this.cropX, y: this.cropY },
      { x: this.cropX + this.cropWidth, y: this.cropY },
      { x: this.cropX, y: this.cropY + this.cropHeight },
      { x: this.cropX + this.cropWidth, y: this.cropY + this.cropHeight }
    ];
    
    corners.forEach(corner => {
      this.ctx.beginPath();
      this.ctx.moveTo(corner.x - (corner.x === this.cropX ? -markerSize/2 : markerSize/2), corner.y);
      this.ctx.lineTo(corner.x + (corner.x === this.cropX ? markerSize/2 : -markerSize/2), corner.y);
      this.ctx.moveTo(corner.x, corner.y - (corner.y === this.cropY ? -markerSize/2 : markerSize/2));
      this.ctx.lineTo(corner.x, corner.y + (corner.y === this.cropY ? markerSize/2 : -markerSize/2));
      this.ctx.stroke();
    });
  }

  // ENHANCED: Draw resize handles - now enabled for circular mode too
  drawResizeHandles() {
    if (!this.isTouchDevice()) return;
    
    const ctx = this.ctx;
    const handleSize = 20;
    const handleColor = '#007aff';
    const handleBorder = '#ffffff';
    const isCircular = this.isCircularMode();
    
    if (isCircular) {
      // For circular mode, draw circular handles at corners of the square crop area
      const handles = [
        { x: this.cropX - handleSize/2, y: this.cropY - handleSize/2 },
        { x: this.cropX + this.cropWidth - handleSize/2, y: this.cropY - handleSize/2 },
        { x: this.cropX - handleSize/2, y: this.cropY + this.cropHeight - handleSize/2 },
        { x: this.cropX + this.cropWidth - handleSize/2, y: this.cropY + this.cropHeight - handleSize/2 }
      ];
      
      handles.forEach(handle => {
        // Draw circular handles for circular mode
        ctx.fillStyle = handleBorder;
        ctx.beginPath();
        ctx.arc(handle.x + handleSize/2, handle.y + handleSize/2, handleSize/2 + 1, 0, Math.PI * 2);
        ctx.fill();
        
        ctx.fillStyle = handleColor;
        ctx.beginPath();
        ctx.arc(handle.x + handleSize/2, handle.y + handleSize/2, handleSize/2, 0, Math.PI * 2);
        ctx.fill();
      });
      
    } else {
      // Rectangular mode - square handles
      const handles = [
        { x: this.cropX - handleSize/2, y: this.cropY - handleSize/2 },
        { x: this.cropX + this.cropWidth - handleSize/2, y: this.cropY - handleSize/2 },
        { x: this.cropX - handleSize/2, y: this.cropY + this.cropHeight - handleSize/2 },
        { x: this.cropX + this.cropWidth - handleSize/2, y: this.cropY + this.cropHeight - handleSize/2 }
      ];
      
      handles.forEach(handle => {
        ctx.fillStyle = handleBorder;
        ctx.fillRect(handle.x - 1, handle.y - 1, handleSize + 2, handleSize + 2);
        
        ctx.fillStyle = handleColor;
        ctx.fillRect(handle.x, handle.y, handleSize, handleSize);
      });
    }
  }

  /* ========================================
     CANVAS INTERACTION - ENHANCED WITH CIRCULAR SUPPORT
     ======================================== */

  setupCanvasEvents() {
    const canvas = this.container.querySelector(`#image-cropper-${this.uniqueId}`);
    if (!canvas) return;

    this.addEventListener(canvas, 'contextmenu', (e) => {
      e.preventDefault();
    });

    if (this.isMobileDevice()) {
      this.addEventListener(canvas, 'touchstart', (e) => {
        e.preventDefault();
      }, { passive: false });
      
      this.addEventListener(canvas, 'touchmove', (e) => {
        e.preventDefault();
      }, { passive: false });
      
      this.addEventListener(canvas, 'touchend', (e) => {
        e.preventDefault();
      }, { passive: false });
    }

    // FIXED: Simplified state management
    const state = {
      isMouseDown: false,
      isDraggingCrop: false,
      isResizingCrop: false,
      isDraggingImage: false,
      dragStartX: 0,
      dragStartY: 0,
      initialCrop: null,
      initialImage: null,
      resizeHandle: null,
      lastTouchDistance: 0,
      isPinching: false,
      initialZoom: 1.0
    };

    const handleStart = (e) => {
      e.preventDefault();
      state.isMouseDown = true;
      
      const coords = this.getCanvasCoordinates(e);
      state.dragStartX = coords.x;
      state.dragStartY = coords.y;
      
      // Check for resize handles first
      state.resizeHandle = this.getCropResizeHandle(coords.x, coords.y);
      
      if (state.resizeHandle) {
        state.isResizingCrop = true;
        // FIXED: Store complete initial state
        state.initialCrop = {
          x: this.cropX,
          y: this.cropY,
          width: this.cropWidth,
          height: this.cropHeight
        };
        canvas.style.cursor = this.getResizeCursor(state.resizeHandle);
      } else if (this.isPointInCropArea(coords.x, coords.y)) {
        // Dragging inside crop area moves the crop area
        state.isDraggingCrop = true;
        state.initialCrop = { x: this.cropX, y: this.cropY };
        canvas.style.cursor = 'move';
      } else {
        // Dragging outside crop area moves the image
        state.isDraggingImage = true;
        // FIXED: Store current image position, not derived base position
        state.initialImage = { x: this.imageX, y: this.imageY };
        canvas.style.cursor = 'grabbing';
      }

      if (e.touches && e.touches.length === 2) {
        state.isPinching = true;
        state.lastTouchDistance = this.getTouchDistance(e.touches[0], e.touches[1]);
        state.initialZoom = this.zoomFactor;
      }
    };

    const handleMove = (e) => {
      e.preventDefault();
      
      const coords = this.getCanvasCoordinates(e);
      
      if (state.isPinching && e.touches && e.touches.length === 2) {
        const currentDistance = this.getTouchDistance(e.touches[0], e.touches[1]);
        const scale = currentDistance / state.lastTouchDistance;
        const newZoom = Math.max(this.minZoom, Math.min(this.maxZoom, state.initialZoom * scale));
        
        if (newZoom !== this.zoomFactor) {
          this.zoomFactor = newZoom;
          this.updateImageDimensions();
          this.drawImage();
          this.updateZoomIndicator();
        }
        return;
      }
      
      if (!state.isMouseDown || state.isPinching) {
        // Update cursor based on hover position
        const handle = this.getCropResizeHandle(coords.x, coords.y);
        if (handle) {
          canvas.style.cursor = this.getResizeCursor(handle);
        } else if (this.isPointInCropArea(coords.x, coords.y)) {
          canvas.style.cursor = 'move';
        } else {
          canvas.style.cursor = 'grab';
        }
        return;
      }
      
      // FIXED: Calculate delta from drag start, not previous position
      const deltaX = coords.x - state.dragStartX;
      const deltaY = coords.y - state.dragStartY;
      
      if (state.isResizingCrop) {
        this.handleCropResize(
          state.resizeHandle, 
          deltaX, 
          deltaY, 
          state.initialCrop.x,
          state.initialCrop.y,
          state.initialCrop.width,
          state.initialCrop.height
        );
      } else if (state.isDraggingCrop) {
        this.handleCropMove(deltaX, deltaY, state.initialCrop.x, state.initialCrop.y);
      } else if (state.isDraggingImage) {
        // FIXED: Use stored initial position consistently
        this.handleImageMove(deltaX, deltaY, state.initialImage.x, state.initialImage.y);
      }
      
      this.drawImage();
    };

    const handleEnd = (e) => {
      e.preventDefault();
      
      // FIXED: Reset all state cleanly
      state.isMouseDown = false;
      state.isDraggingCrop = false;
      state.isResizingCrop = false;
      state.isDraggingImage = false;
      state.isPinching = false;
      state.resizeHandle = null;
      state.initialCrop = null;
      state.initialImage = null;
      
      canvas.style.cursor = 'grab';
    };

    // Attach events using the same method you're already using
    this.addEventListener(canvas, 'mousedown', handleStart);
    this.addEventListener(canvas, 'mousemove', handleMove);
    this.addEventListener(canvas, 'mouseup', handleEnd);
    this.addEventListener(canvas, 'mouseleave', handleEnd);

    this.addEventListener(canvas, 'touchstart', handleStart);
    this.addEventListener(canvas, 'touchmove', handleMove);
    this.addEventListener(canvas, 'touchend', handleEnd);

    // FIXED: Zoom handling that doesn't interfere with positioning
    this.addEventListener(canvas, 'wheel', (e) => {
      e.preventDefault();
      
      const zoomSensitivity = 0.1;
      const zoomDirection = e.deltaY < 0 ? 1 : -1;
      const zoomChange = zoomDirection * zoomSensitivity;
      
      const newZoom = Math.max(this.minZoom, Math.min(this.maxZoom, this.zoomFactor + zoomChange));
      
      if (newZoom !== this.zoomFactor) {
        this.zoomFactor = newZoom;
        this.updateImageDimensions();
        this.drawImage();
        this.updateZoomIndicator();
      }
    }, { passive: false });

    // Add double-click to fit
    this.addEventListener(canvas, 'dblclick', () => {
      this.fitToArea();
    });
  }

  /* ========================================
     INTERACTION HANDLING - ENHANCED WITH CIRCULAR SUPPORT
     ======================================== */

  handleImageMove(deltaX, deltaY, initialX, initialY) {
    const newX = initialX + deltaX;
    const newY = initialY + deltaY;
    
    // Keep image in reasonable bounds relative to canvas
    const minX = -this.imageWidth * 0.8;
    const maxX = this.canvas.width - this.imageWidth * 0.2;
    const minY = -this.imageHeight * 0.8;
    const maxY = this.canvas.height - this.imageHeight * 0.2;
    
    this.imageX = Math.max(minX, Math.min(maxX, newX));
    this.imageY = Math.max(minY, Math.min(maxY, newY));
    
  }

  handleCropMove(deltaX, deltaY, initialX, initialY) {
    // Move crop area (works for both rectangular and circular)
    let newX = initialX + deltaX;
    let newY = initialY + deltaY;
    
    newX = Math.max(0, Math.min(newX, this.canvas.width - this.cropWidth));
    newY = Math.max(0, Math.min(newY, this.canvas.height - this.cropHeight));
    
    this.cropX = newX;
    this.cropY = newY;
  }

  // ENHANCED: Handle crop resize - now works for circular mode too
  handleCropResize(handle, deltaX, deltaY, initialX, initialY, initialWidth, initialHeight) {
    const isCircular = this.isCircularMode();
    
    let newWidth = initialWidth;
    let newHeight = initialHeight;
    let newX = initialX;
    let newY = initialY;
    
    if (isCircular) {
      // For circular mode, maintain square aspect ratio
      switch (handle) {
        case 'se':
          newWidth = initialWidth + deltaX;
          newHeight = newWidth; // Keep square
          break;
        case 'sw':
          newWidth = initialWidth - deltaX;
          newHeight = newWidth; // Keep square
          newX = initialX + (initialWidth - newWidth);
          break;
        case 'ne':
          newWidth = initialWidth + deltaX;
          newHeight = newWidth; // Keep square
          newY = initialY + (initialHeight - newHeight);
          break;
        case 'nw':
          newWidth = initialWidth - deltaX;
          newHeight = newWidth; // Keep square
          newX = initialX + (initialWidth - newWidth);
          newY = initialY + (initialHeight - newHeight);
          break;
      }
    } else {
      // Rectangular mode - use aspect ratio
      const aspectRatio = this.getAspectRatio();
      
      switch (handle) {
        case 'se':
          newWidth = initialWidth + deltaX;
          newHeight = newWidth / aspectRatio;
          break;
        case 'sw':
          newWidth = initialWidth - deltaX;
          newHeight = newWidth / aspectRatio;
          newX = initialX + (initialWidth - newWidth);
          break;
        case 'ne':
          newWidth = initialWidth + deltaX;
          newHeight = newWidth / aspectRatio;
          newY = initialY + (initialHeight - newHeight);
          break;
        case 'nw':
          newWidth = initialWidth - deltaX;
          newHeight = newWidth / aspectRatio;
          newX = initialX + (initialWidth - newWidth);
          newY = initialY + (initialHeight - newHeight);
          break;
      }
    }
    
    const minSize = 50;
    const maxWidth = this.canvas.width;
    const maxHeight = this.canvas.height;
    
    if (newWidth >= minSize && newWidth <= maxWidth && 
        newHeight >= minSize && newHeight <= maxHeight &&
        newX >= 0 && newX + newWidth <= this.canvas.width &&
        newY >= 0 && newY + newHeight <= this.canvas.height) {
      
      this.cropWidth = newWidth;
      this.cropHeight = newHeight;
      this.cropX = newX;
      this.cropY = newY;
    }
  }

  // ENHANCED: Get crop resize handle - now works for circular mode
  getCropResizeHandle(mouseX, mouseY) {
    const tolerance = this.isTouchDevice() ? 25 : 10;
    
    // Check all four corners for both circular and rectangular
    if (Math.abs(mouseX - this.cropX) <= tolerance && Math.abs(mouseY - this.cropY) <= tolerance) {
      return 'nw';
    }
    if (Math.abs(mouseX - (this.cropX + this.cropWidth)) <= tolerance && Math.abs(mouseY - this.cropY) <= tolerance) {
      return 'ne';
    }
    if (Math.abs(mouseX - this.cropX) <= tolerance && Math.abs(mouseY - (this.cropY + this.cropHeight)) <= tolerance) {
      return 'sw';
    }
    if (Math.abs(mouseX - (this.cropX + this.cropWidth)) <= tolerance && Math.abs(mouseY - (this.cropY + this.cropHeight)) <= tolerance) {
      return 'se';
    }
    
    return null;
  }

  isPointInCropArea(x, y) {
    const isCircular = this.isCircularMode();
    
    if (isCircular) {
      // For circular mode, check if point is inside the circle
      const centerX = this.cropX + this.cropWidth / 2;
      const centerY = this.cropY + this.cropHeight / 2;
      const radius = Math.min(this.cropWidth, this.cropHeight) / 2;
      
      const distance = Math.sqrt(
        Math.pow(x - centerX, 2) + Math.pow(y - centerY, 2)
      );
      
      return distance <= radius;
    } else {
      // Rectangular mode
      return x >= this.cropX && x <= this.cropX + this.cropWidth &&
             y >= this.cropY && y <= this.cropY + this.cropHeight;
    }
  }

  getResizeCursor(handle) {
    const cursors = {
      'nw': 'nw-resize', 'ne': 'ne-resize', 'se': 'se-resize', 'sw': 'sw-resize'
    };
    return cursors[handle] || 'default';
  }

  getCanvasCoordinates(e) {
    const rect = this.canvas.getBoundingClientRect();
    
    let clientX, clientY;
    if (e.touches && e.touches.length > 0) {
      clientX = e.touches[0].clientX;
      clientY = e.touches[0].clientY;
    } else if (e.changedTouches && e.changedTouches.length > 0) {
      clientX = e.changedTouches[0].clientX;
      clientY = e.changedTouches[0].clientY;
    } else {
      clientX = e.clientX;
      clientY = e.clientY;
    }
    
    return {
      x: (clientX - rect.left) * (this.canvas.width / rect.width),
      y: (clientY - rect.top) * (this.canvas.height / rect.height)
    };
  }

  getTouchDistance(touch1, touch2) {
    const dx = touch1.clientX - touch2.clientX;
    const dy = touch1.clientY - touch2.clientY;
    return Math.sqrt(dx * dx + dy * dy);
  }

  /* ========================================
     IMAGE CONTROL FUNCTIONS
     ======================================== */

  fitToArea() {
    if (!this.image) return;
    
    engravingLogger.dev('🎯 Fitting image to crop area...');
    
    const isCircular = this.isCircularMode();
    
    if (isCircular) {
      // For circular mode, fit image to fill the circular crop area
      const cropCenterX = this.cropX + this.cropWidth / 2;
      const cropCenterY = this.cropY + this.cropHeight / 2;
      const cropRadius = Math.min(this.cropWidth, this.cropHeight) / 2;
      const targetDiameter = cropRadius * 2;
      
      // Calculate scale needed to fit image to circle
      const imageSize = Math.min(this.image.width, this.image.height);
      const currentImageSize = Math.min(this.imageWidth, this.imageHeight);
      const scaleNeeded = targetDiameter / currentImageSize;
      
      // Apply the scale through zoom
      this.zoomFactor = Math.max(this.minZoom, Math.min(this.maxZoom, this.zoomFactor * scaleNeeded));
      this.updateImageDimensions();
      
      // Center the image on the crop area
      this.imageX = cropCenterX - this.imageWidth / 2;
      this.imageY = cropCenterY - this.imageHeight / 2;
      
    } else {
      // For rectangular mode, fit image to fill the rectangular crop area
      const cropCenterX = this.cropX + this.cropWidth / 2;
      const cropCenterY = this.cropY + this.cropHeight / 2;
      
      // Calculate scale needed to fit image to crop area
      const scaleX = this.cropWidth / this.imageWidth;
      const scaleY = this.cropHeight / this.imageHeight;
      const scaleNeeded = Math.max(scaleX, scaleY); // Use max to fill the area
      
      // Apply the scale through zoom
      this.zoomFactor = Math.max(this.minZoom, Math.min(this.maxZoom, this.zoomFactor * scaleNeeded));
      this.updateImageDimensions();
      
      // Center the image on the crop area
      this.imageX = cropCenterX - this.imageWidth / 2;
      this.imageY = cropCenterY - this.imageHeight / 2;
    }
    
    this.drawImage();
    this.updateZoomIndicator();
    
    const modeText = isCircular ? 'circle' : 'crop area';
    this.showMessage(`✓ Image fitted to ${modeText}`, 'info', 2000);
    
    engravingLogger.dev(`🎯 Image fitted to ${modeText}`);
  }

  centerImage() {
    if (!this.image) return;
    
    engravingLogger.dev('📍 Centering image...');
    
    const isCircular = this.isCircularMode();
    
    if (isCircular) {
      // Center the image on the circular crop area
      const cropCenterX = this.cropX + this.cropWidth / 2;
      const cropCenterY = this.cropY + this.cropHeight / 2;
      
      this.imageX = cropCenterX - this.imageWidth / 2;
      this.imageY = cropCenterY - this.imageHeight / 2;
    } else {
      // Center the image on the rectangular crop area
      if (this.cropWidth && this.cropHeight) {
        const cropCenterX = this.cropX + this.cropWidth / 2;
        const cropCenterY = this.cropY + this.cropHeight / 2;
        
        this.imageX = cropCenterX - this.imageWidth / 2;
        this.imageY = cropCenterY - this.imageHeight / 2;
      } else {
        // If no crop area, center on canvas
        this.imageX = (this.canvas.width - this.imageWidth) / 2;
        this.imageY = (this.canvas.height - this.imageHeight) / 2;
      }
    }
    
    this.drawImage();
    this.showMessage('✓ Image centered', 'info', 2000);
    
    engravingLogger.dev(`📍 Image centered at (${this.imageX}, ${this.imageY})`);
  }

  resetCrop() {
    if (!this.image) return;
    
    engravingLogger.dev('🔄 Resetting crop and image position...');
    
    // Reset zoom factor first
    this.zoomFactor = 1.0;
    
    // Recalculate and center the image
    this.fitImageToCanvas();
    
    // Reinitialize crop area
    this.initializeCropArea();
    
    // Redraw everything
    this.drawImage();
    this.updateZoomIndicator();
    
    // Reset grayscale
    this.isGrayscale = false;
    const grayscaleBtn = this.container.querySelector(`#grayscale-toggle-${this.uniqueId}`);
    if (grayscaleBtn) {
      grayscaleBtn.textContent = 'Toggle Monochrome';
      grayscaleBtn.classList.remove('active');
      grayscaleBtn.setAttribute('aria-pressed', 'false');
    }

    this.showMessage('✓ Reset to default position and settings', 'info', 2000);
    
    engravingLogger.dev('🔄 Reset complete');
  }

  rotateImage() {
    if (!this.image) return;
    engravingLogger.dev('🔄 Rotating image 90° clockwise...');

    // Rotate the SOURCE image 90° CW into an offscreen canvas, then swap it in
    // as the new this.image. A canvas is a valid drawImage source and exposes
    // .width/.height, so every fit/crop calculation keeps working unchanged —
    // the dimensions simply swap (w↔h), which is exactly what a 90° turn needs.
    const src = this.image;
    const rotated = document.createElement('canvas');
    rotated.width = src.height;
    rotated.height = src.width;
    const rctx = rotated.getContext('2d');
    rctx.imageSmoothingEnabled = true;
    rctx.imageSmoothingQuality = 'high';
    rctx.translate(rotated.width / 2, rotated.height / 2);
    rctx.rotate(Math.PI / 2);                 // 90° clockwise
    rctx.drawImage(src, -src.width / 2, -src.height / 2);

    this.image = rotated;

    // Re-fit to the canvas and reset zoom/crop so the rotated image is framed
    // cleanly (same path the module uses after a fresh upload).
    this.zoomFactor = 1;
    this.fitImageToCanvas();
    this.initializeCropArea();
    this.drawImage();

    if (typeof this.saveState === 'function') { try { this.saveState(); } catch (e) {} }
    this.showMessage('✓ Image rotated', 'info', 1200);
  }

  toggleGrayscale() {
    this.isGrayscale = !this.isGrayscale;
    this.drawImage();
    
    const button = this.container.querySelector(`#grayscale-toggle-${this.uniqueId}`);
    if (button) {
      button.textContent = this.isGrayscale ? 'Show Original' : 'Toggle Monochrome';
      button.classList.toggle('active', this.isGrayscale);
      button.setAttribute('aria-pressed', this.isGrayscale.toString());
    }

    const modeText = this.isGrayscale ? 'monochrome' : 'original';
    this.showMessage(`✓ Switched to ${modeText} mode`, 'info', 1500);
  }

  removeImage() {
    engravingLogger.dev('🗑️ Removing image...');
    
    this.uploadedFile = null;
    this.croppedImageData = null;
    this.image = null;
  
    if (this.canvas) {
      this.canvas.width = 1;
      this.canvas.height = 1;
      this.canvas.style.width = '1px';
      this.canvas.style.height = '1px';
    }
  
    this.hideImagePreview();
  
    const fileInput = this.container.querySelector(`#engraving-file-${this.uniqueId}`);
    if (fileInput) {
      fileInput.value = '';
    }
    
    this.isGrayscale = false;
    const grayscaleBtn = this.container.querySelector(`#grayscale-toggle-${this.uniqueId}`);
    if (grayscaleBtn) {
      grayscaleBtn.textContent = 'Toggle Monochrome';
      grayscaleBtn.classList.remove('active');
      grayscaleBtn.setAttribute('aria-pressed', 'false');
    }

    this.updateUI();
    this.showMessage('✓ Image removed', 'info', 2000);
  }

  showImagePreview() {
    const previewContainer = this.container.querySelector('.image-preview-container');
    const zoomIndicator = this.container.querySelector('.zoom-indicator');
    if (previewContainer) {
      previewContainer.style.display = 'block';
      if (zoomIndicator) zoomIndicator.style.display = 'block';
      this.drawImage();
      this.updateZoomIndicator();
    }
  }

  hideImagePreview() {
    const previewContainer = this.container.querySelector('.image-preview-container');
    const zoomIndicator = this.container.querySelector('.zoom-indicator');
    if (previewContainer) {
      previewContainer.style.display = 'none';
    }
    if (zoomIndicator) {
      zoomIndicator.style.display = 'none';
    }
  }

  updateZoomIndicator() {
    const percentage = Math.round(this.zoomFactor * 100);
    const zoomLevelElements = document.querySelectorAll(`#zoom-level-${this.uniqueId}, .zoom-level`);
    
    zoomLevelElements.forEach((element) => {
      element.textContent = percentage + '%';
    });
  }

  /* ========================================
     IMAGE CROPPING & OUTPUT
     ======================================== */

  getCroppedImageData() {
    if (!this.image || !this.ctx) return null;

    const isCircular = this.isCircularMode();
    
    if (isCircular) {
      return this.getCircularCroppedImageData();
    } else {
      return this.getRectangularCroppedImageData();
    }
  }

  getCircularCroppedImageData() {
    // Get the dimensions of the crop area
    const cropCenterX = this.cropX + this.cropWidth / 2;
    const cropCenterY = this.cropY + this.cropHeight / 2;
    const radius = Math.min(this.cropWidth, this.cropHeight) / 2;
    
    const outputSize = 400;
    const outputRadius = outputSize / 2;
    
    const croppedCanvas = document.createElement('canvas');
    const croppedCtx = croppedCanvas.getContext('2d');
    
    croppedCanvas.width = outputSize;
    croppedCanvas.height = outputSize;

    croppedCtx.imageSmoothingEnabled = true;
    croppedCtx.imageSmoothingQuality = 'high';

    if (this.isGrayscale) {
      croppedCtx.filter = 'grayscale(100%)';
    }
    
    // Create circular clipping path
    croppedCtx.beginPath();
    croppedCtx.arc(outputRadius, outputRadius, outputRadius, 0, Math.PI * 2);
    croppedCtx.clip();

    // Calculate source coordinates from the circular crop area
    const sourceX = cropCenterX - radius;
    const sourceY = cropCenterY - radius;
    const sourceSize = radius * 2;
    
    // Map canvas coordinates to image coordinates
    const scaleRatio = this.image.width / this.imageWidth;
    const imageSourceX = (sourceX - this.imageX) * scaleRatio;
    const imageSourceY = (sourceY - this.imageY) * scaleRatio;
    const imageSourceSize = sourceSize * scaleRatio;

    // Draw the circular portion to fill the output circle
    croppedCtx.drawImage(
      this.image,
      Math.max(0, imageSourceX),
      Math.max(0, imageSourceY),
      Math.min(imageSourceSize, this.image.width - Math.max(0, imageSourceX)),
      Math.min(imageSourceSize, this.image.height - Math.max(0, imageSourceY)),
      0, 0, outputSize, outputSize
    );

    return croppedCanvas.toDataURL('image/png', 0.9);
  }

  getRectangularCroppedImageData() {
    const scaleRatio = this.image.width / this.imageWidth;
    const cropImageX = (this.cropX - this.imageX) * scaleRatio;
    const cropImageY = (this.cropY - this.imageY) * scaleRatio;
    const cropImageWidth = this.cropWidth * scaleRatio;
    const cropImageHeight = this.cropHeight * scaleRatio;

    const croppedCanvas = document.createElement('canvas');
    const croppedCtx = croppedCanvas.getContext('2d');
    
    croppedCanvas.width = this.cropWidth;
    croppedCanvas.height = this.cropHeight;

    croppedCtx.imageSmoothingEnabled = true;
    croppedCtx.imageSmoothingQuality = 'high';

    if (this.isGrayscale) {
      croppedCtx.filter = 'grayscale(100%)';
    }

    croppedCtx.drawImage(
      this.image,
      Math.max(0, cropImageX),
      Math.max(0, cropImageY),
      Math.min(cropImageWidth, this.image.width - Math.max(0, cropImageX)),
      Math.min(cropImageHeight, this.image.height - Math.max(0, cropImageY)),
      Math.max(0, -cropImageX / scaleRatio),
      Math.max(0, -cropImageY / scaleRatio),
      Math.min(this.cropWidth, (this.image.width - Math.max(0, cropImageX)) / scaleRatio),
      Math.min(this.cropHeight, (this.image.height - Math.max(0, cropImageY)) / scaleRatio)
    );

    return croppedCanvas.toDataURL('image/png', 0.9);
  }

  /* ========================================
     QUANTITY MANAGEMENT
     ======================================== */

  handleQuantityChange(button) {
    return this.measurePerformance('quantity_change', () => {
      const variantId = button.dataset.variant;
      
      const inputs = this.container.querySelectorAll(`input[data-variant="${variantId}"]`);
      const isPlus = button.classList.contains('plus');
      const currentValue = parseInt(inputs[0]?.value) || 0;
      
      let newValue;
      if (isPlus) {
        newValue = currentValue + 1;
      } else {
        newValue = Math.max(0, currentValue - 1);
      }
      
      const tempInput = inputs[0].cloneNode();
      tempInput.value = newValue;
      if (!this.validateQuantityInput(tempInput)) {
        return;
      }
      
      inputs.forEach(input => {
        input.value = newValue;
      });
      
      this.debouncedUpdateVariant(variantId, newValue);
    });
  }

  initQuantityDebouncing() {
    this.addEventListener(this.container, 'input', (e) => {
      if (e.target.classList.contains('qty-input')) {
        this.safeExecute(() => {
          const variantId = e.target.dataset.variant;
          let quantity = parseInt(e.target.value) || 0;
          
          if (this.validateQuantityInput(e.target)) {
            this.syncQuantityInputs(variantId, quantity);
            this.debouncedUpdateVariant(variantId, quantity);
          }
        }, 'Quantity input change');
      }
    });

    this.addEventListener(this.container, 'blur', (e) => {
      if (e.target.classList.contains('qty-input')) {
        this.safeExecute(() => {
          this.validateQuantityInput(e.target);
        }, 'Quantity input validation');
      }
    }, true);
  }

  syncQuantityInputs(variantId, newValue) {
    const allInputs = this.container.querySelectorAll(`input[data-variant="${variantId}"]`);
    allInputs.forEach(input => {
      if (input.value !== newValue.toString()) {
        input.value = newValue;
      }
    });
  }

  updateVariantSelection(variantId, quantity) {
    return this.measurePerformance('variant_selection_update', () => {
      this.toggleRowLoading(variantId, true);
      
      const row = this.container.querySelector(`[data-variant-id="${variantId}"]`);
      
      if (quantity > 0) {
        this.selectedVariants.set(variantId, quantity);
        row?.classList.add('selected');
      } else {
        this.selectedVariants.delete(variantId);
        row?.classList.remove('selected');
      }
      
      this.updateVariantTotal(variantId, quantity);
      this.updateUI();

      const variantName = row?.querySelector('.variant-title')?.textContent || 'Item';
      if (quantity > 0) {
        this.updateLiveRegions(`${variantName} quantity updated to ${quantity}`);
      } else {
        this.updateLiveRegions(`${variantName} removed from selection`);
      }
      
      this.saveState();
      
      setTimeout(() => {
        this.toggleRowLoading(variantId, false);
      }, 200);
    });
  }

  updateVariantTotal(variantId, quantity) {
    const variantRow = this.container.querySelector(`[data-variant-id="${variantId}"]`);
    const price = parseFloat(variantRow?.dataset.variantPrice) || 0;
    const total = (price / 100) * quantity;
    
    const totalElements = variantRow?.querySelectorAll('[data-variant-total]');
    totalElements?.forEach(totalElement => {
      totalElement.textContent = this.formatMoney(total);
    });
    
    this.updateProductSubtotal();
  }

  updateProductSubtotal() {
    let subtotal = 0;
    
    this.selectedVariants.forEach((quantity, variantId) => {
      const variantRow = this.container.querySelector(`[data-variant-id="${variantId}"]`);
      const price = parseFloat(variantRow?.dataset.variantPrice) || 0;
      subtotal += (price / 100) * quantity;
    });
    
    const subtotalElement = this.container.querySelector('.subtotal-amount');
    if (subtotalElement) {
      subtotalElement.textContent = this.formatMoney(subtotal);
    }
  }

  /* ========================================
     INPUT VALIDATION
     ======================================== */

  validateInputs() {
    const errors = [];
    
    if (this.uploadedFile) {
      const maxSize = (this.settings.maxFileSize || 5) * 1024 * 1024;
      if (this.uploadedFile.size > maxSize) {
        errors.push(`File size exceeds ${this.settings.maxFileSize || 5}MB limit`);
      }
      
      const validTypes = ['image/jpeg', 'image/jpg', 'image/png'];
      if (!validTypes.includes(this.uploadedFile.type)) {
        errors.push('Invalid file type. Please use JPG, JPEG, or PNG.');
      }
    }
    
    if (this.selectedVariants.size === 0) {
      errors.push('Please select at least one variant');
    }
    
    this.selectedVariants.forEach((quantity, variantId) => {
      if (quantity <= 0) {
        errors.push(`Invalid quantity for variant ${variantId}`);
      }
    });
    
    return errors;
  }

  validateQuantityInput(input) {
    const value = parseInt(input.value);
    const min = parseInt(input.dataset.min || input.min || 0);
    const max = parseInt(input.dataset.max || input.max || 999);
    
    const variantId = input.dataset.variant;
    const variantRow = this.container.querySelector(`[data-variant-id="${variantId}"]`);
    const isAvailable = variantRow?.dataset.variantAvailable === 'true';
    
    input.setCustomValidity('');
    
    if (!isAvailable && value > 0) {
      this.showValidationError(input, 'This variant is out of stock');
      return false;
    }
    
    if (input.value === '' || input.value === '0') {
      this.clearValidationError(input);
      return true;
    }
    
    if (isNaN(value)) {
      this.showValidationError(input, 'Please enter a valid number');
      return false;
    }
    
    if (value < min) {
      this.showValidationError(input, `Minimum quantity is ${min}`);
      return false;
    }
    
    if (value > max) {
      this.showValidationError(input, `Maximum quantity is ${max}`);
      return false;
    }
    
    const inventory = parseInt(variantRow?.dataset.variantInventory || 999);
    if (inventory > 0 && value > inventory) {
      this.showValidationError(input, `Only ${inventory} available in stock`);
      return false;
    }
    
    this.clearValidationError(input);
    return true;
  }

  showValidationError(input, message) {
    const variantRow = input.closest('.variant-row');
    let errorEl = variantRow.querySelector('.quantity-error');
    
    if (!errorEl) {
      errorEl = document.createElement('div');
      errorEl.className = 'quantity-error';
      errorEl.setAttribute('role', 'alert');
      errorEl.setAttribute('aria-live', 'assertive');
      input.closest('.quantity-selector').style.position = 'relative';
      input.closest('.quantity-selector').appendChild(errorEl);
    }
    
    errorEl.textContent = message;
    errorEl.style.display = 'block';
    
    setTimeout(() => this.clearValidationError(input), 3000);
  }

  clearValidationError(input) {
    const errorEl = input.closest('.quantity-selector')?.querySelector('.quantity-error');
    if (errorEl) {
      errorEl.style.display = 'none';
    }
  }

  /* ========================================
     ADD TO CART
     ======================================== */

  async addToCart() {
    return this.measureAsyncPerformance('add_to_cart_full', async () => {
      if (this.selectedVariants.size === 0) return;

      const validationErrors = this.validateInputs();
      if (validationErrors.length > 0) {
        this.showMessage(`⚠ ${validationErrors.join('. ')}`, 'warning');
        return;
      }

      if (!this.validateAddToCart()) {
        return;
      }

      this.showLoading();

      try {
        let tempImageData = null;
        
        if (this.uploadedFile) {
          tempImageData = await this.storeImageTemporarily();
        }

        const cartItems = await this.addItemsToShopifyCart(tempImageData);
        
        this.hideLoading();
        
        const totalItems = Array.from(this.selectedVariants.values()).reduce((sum, qty) => sum + qty, 0);
        this.showMessage(`✓ ${totalItems} item${totalItems > 1 ? 's' : ''} added to cart successfully!`, 'success');
        this.updateLiveRegions(`${totalItems} items successfully added to cart`);
        
        this.resetModule();

      } catch (error) {
        this.hideLoading();
        
        const errorMessage = this.getHelpfulErrorMessage(error.message);
        this.showMessage(`⚠ ${errorMessage}`, 'error');
        this.updateLiveRegions(`Error adding items to cart: ${errorMessage}`);
      }
    });
  }

  validateAddToCart() {
    if (this.selectedVariants.size === 0) {
      this.showMessage('⚠ Please select at least one item', 'warning');
      return false;
    }

    if (!this.uploadedFile) {
      this.showMessage('⚠ Please upload an engraving design', 'warning');
      return false;
    }

    return true;
  }

  /* ============================================
   UPDATED storeImageTemporarily FUNCTION
   Add this to replace your existing function
   ============================================ */

async storeImageTemporarily() {
  try {
    // Show initial progress
    window.showProgress('Processing image...', 0);
    
    // STEP 1: Get the cropped image data
    const croppedImageData = this.getCroppedImageData();
    if (!croppedImageData) {
      window.hideProgress();
      throw new Error('Failed to process image');
    }
    window.updateProgress(15, 'Processing...');

    // STEP 2: Generate unique image ID
    const imageId = `temp_${this.productId}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    engravingLogger.dev('📦 Generated image ID:', imageId);
    
    // STEP 3: Create metadata
    const metadata = {
      originalName: this.uploadedFile.name,
      productId: this.productId,
      isGrayscale: this.isGrayscale,
      cropRatio: this.settings.cropRatio,
      cropShape: this.settings.cropShape,
      timestamp: new Date().toISOString()
    };

    // STEP 4: Save to browser storage if available
    if (this.browserStorageAvailable) {
      await this.browserStorage.storeImage(imageId, croppedImageData, metadata);
      engravingLogger.dev('✅ Saved to browser storage');
    }
    window.updateProgress(40, '🔒 Verifying security...');

    // STEP 5: Get reCAPTCHA token - WITH BETTER ERROR HANDLING
    engravingLogger.dev('🔒 Getting reCAPTCHA token for engraving upload...');
    let recaptchaToken;
    try {
      recaptchaToken = await getUniversalRecaptchaToken(
        window.UNIVERSAL_RECAPTCHA_CONFIG.actions.FILE_UPLOAD
      );
      
      if (!recaptchaToken || recaptchaToken.length < 10) {
        throw new Error('Invalid reCAPTCHA token received');
      }
      
      engravingLogger.dev('✅ reCAPTCHA token obtained:', recaptchaToken.substring(0, 20) + '...');
    } catch (error) {
      window.hideProgress();
      engravingLogger.error('❌ reCAPTCHA failed:', error);
      throw new Error('Security verification failed. Please refresh the page and try again.');
    }
    
    window.updateProgress(60, '☁️ Uploading please be patient...');

    // STEP 6: Prepare upload payload - WITH EXPLICIT RECAPTCHA TOKEN
    const uploadPayload = {
      action: 'uploadEngravingDesign',
      source: 'product_page',
      fileData: croppedImageData.split(',')[1],
      fileName: `design_${imageId}.png`,
      fileType: 'image/png',
      designId: imageId,
      productId: this.productId,
      productTitle: this.settings.productData?.title || 'Custom Item',
      cropShape: this.settings.cropShape,
      cropRatio: this.settings.cropRatio,
      recaptchaToken: recaptchaToken  // Explicitly include token
    };

    // STEP 6.5: LOG THE PAYLOAD (for debugging)
    engravingLogger.dev('📦 Upload payload:', {
      action: uploadPayload.action,
      fileName: uploadPayload.fileName,
      designId: uploadPayload.designId,
      hasRecaptcha: !!uploadPayload.recaptchaToken,
      recaptchaLength: uploadPayload.recaptchaToken ? uploadPayload.recaptchaToken.length : 0,
      fileDataLength: uploadPayload.fileData ? uploadPayload.fileData.length : 0
    });

    // STEP 7: Upload to server WITH BETTER ERROR HANDLING
    engravingLogger.dev('📤 Uploading please be patient...');
    
    const response = await fetch('https://engraverslabdrive.nick-aff.workers.dev/', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(uploadPayload)
    });

    window.updateProgress(90, '✨ Finalizing...');

    // STEP 8: Parse response WITH DETAILED ERROR LOGGING
    engravingLogger.dev('📡 Response status:', response.status);
    
    if (!response.ok) {
      // Try to get error details from response
      let errorMessage = `Upload failed: ${response.status}`;
      try {
        const errorData = await response.json();
        errorMessage = errorData.message || errorMessage;
        engravingLogger.error('❌ Server error details:', errorData);
      } catch (e) {
        // If we can't parse the error response, just use the status
        const errorText = await response.text();
        engravingLogger.error('❌ Server error text:', errorText);
      }
      
      window.hideProgress();
      throw new Error(errorMessage);
    }

    const result = await response.json();
    engravingLogger.dev('📥 Server response:', result);

    if (!result.success) {
      engravingLogger.error('❌ Server returned error:', result.message);
      window.hideProgress();
      throw new Error(result.message || 'Upload failed');
    }

    // STEP 9: Build the complete image data object
    const imageData = {
      tempId: imageId,
      fileName: result.fileName || `design_${imageId}.png`,
      dataUrl: croppedImageData,
      storedAt: new Date().toISOString(),
      metadata: metadata,
      driveFileId: result.fileId || null,
      driveUrl: result.fileUrl || null,
      uploadedToDrive: !!result.success,
      hasBrowserBackup: this.browserStorageAvailable,
      hasDriveBackup: !!result.success
    };

    engravingLogger.dev('📦 Built image data object:', {
      tempId: imageData.tempId,
      fileName: imageData.fileName,
      driveFileId: imageData.driveFileId,
      hasDriveBackup: imageData.hasDriveBackup
    });

    // Show success and hide progress
    window.showProgressSuccess('✅ Upload complete!');

    engravingLogger.dev('✅ Returning from storeImageTemporarily:', {
      tempId: imageData.tempId,
      fileName: imageData.fileName,
      driveFileId: imageData.driveFileId,
      hasDriveBackup: imageData.hasDriveBackup
    });

    return imageData;

  } catch (error) {
    window.hideProgress();
    engravingLogger.error('❌ Store image error:', error);
    
    // Give user a helpful error message based on the error
    if (error.message.includes('Security verification')) {
      throw new Error('Security verification failed. Please refresh the page and try again.');
    } else if (error.message.includes('500')) {
      throw new Error('Server error. Please try again in a moment.');
    } else if (error.message.includes('Failed to process image')) {
      throw new Error('Image processing failed. Please try a different image.');
    } else {
      throw new Error(`Upload failed: ${error.message}`);
    }
  }
}

  async addItemsToShopifyCart(tempImageData) {
    const cartItems = [];
    
    for (const [variantId, quantity] of this.selectedVariants) {
      const lineItemId = `engraving_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      
      const cartItem = {
        id: variantId,
        quantity: quantity,
        properties: {
          '_engraving_enabled': 'true',
          '_engraving_item_id': lineItemId,
          '_engraving_crop_ratio': this.settings.cropRatio || '1:1',
          '_engraving_crop_shape': this.settings.cropShape || 'rectangular',
          '_product_title': this.settings.productData?.title || 'Custom Item',
          '_product_id': this.productId
        }
      };

      if (tempImageData) {
        cartItem.properties._engraving_temp_id = tempImageData.tempId;
        cartItem.properties._engraving_file_name = tempImageData.fileName;
        cartItem.properties._temp_stored_at = tempImageData.storedAt;
        
        if (tempImageData.driveFileId) {
          cartItem.properties._engraving_drive_file_id = tempImageData.driveFileId;
          cartItem.properties._engraving_drive_url = tempImageData.driveUrl;
          cartItem.properties._engraving_status = 'drive_ready';
        } else {
          cartItem.properties._engraving_status = 'drive_upload_failed';
        }
        
        cartItem.properties._has_drive_backup = tempImageData.hasDriveBackup ? 'true' : 'false';
      }

      cartItems.push(cartItem);
    }

    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: cartItems })
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to add items to cart: ${response.status} ${errorText}`);
    }

    const result = await response.json();
    return result.items || cartItems;
  }

  getHelpfulErrorMessage(originalError) {
    if (originalError.includes('413') || originalError.includes('too large')) {
      return 'File is too large. Please try a smaller image or reduce quality.';
    } else if (originalError.includes('network') || originalError.includes('fetch')) {
      return 'Network error. Please check your internet connection and try again.';
    } else if (originalError.includes('timeout')) {
      return 'Upload timed out. Please try again with a smaller file.';
    } else if (originalError.includes('400')) {
      return 'Invalid file format. Please use JPG, JPEG, or PNG files only.';
    } else if (originalError.includes('500')) {
      return 'Server error. Please try again in a moment.';
    } else {
      return 'Upload failed. Please try again or contact support if the problem persists.';
    }
  }

  /* ========================================
     UI UPDATES
     ======================================== */

  updateUI() {
    this.safeExecute(() => {
      this.updateTotalItems();
      this.updateAddToCartButton();
      this.updateProgressState();
    }, 'UI update');
  }

  updateProgressState() {
    this.progressState.fileUploaded = !!this.uploadedFile;
    this.progressState.variantsSelected = this.selectedVariants.size > 0;
    this.progressState.readyToAddToCart = this.progressState.fileUploaded && this.progressState.variantsSelected;
  }

  updateTotalItems() {
    const itemCountElement = this.container.querySelector('.item-count');
    if (!itemCountElement) return;

    let totalItems = 0;
    this.selectedVariants.forEach(quantity => {
      totalItems += quantity;
    });

    itemCountElement.textContent = totalItems;
    itemCountElement.setAttribute('aria-label', `${totalItems} items selected`);
  }

  updateAddToCartButton() {
    const button = this.container.querySelector('.add-to-cart-btn');
    if (!button) return;

    const hasSelections = this.selectedVariants.size > 0;
    const hasFile = !!this.uploadedFile;
    
    button.disabled = !hasSelections || !hasFile;
    
    if (hasSelections && hasFile) {
      const totalItems = Array.from(this.selectedVariants.values()).reduce((sum, qty) => sum + qty, 0);
      if (totalItems === 1) {
        button.textContent = 'Add 1 Item to Cart';
      } else {
        button.textContent = `Add ${totalItems} Items to Cart`;
      }
    } else if (hasSelections && !hasFile) {
      button.textContent = 'Upload Design to Continue';
    } else if (!hasSelections && hasFile) {
      button.textContent = 'Select Items to Continue';
    } else {
      button.textContent = 'Add to Cart';
    }
  }

  resetModule() {
    this.selectedVariants.clear();
    
    this.container.querySelectorAll('.qty-input').forEach(input => {
      input.value = 0;
    });
    
    this.container.querySelectorAll('.variant-row').forEach(row => {
      row.classList.remove('selected');
    });
    
    this.removeImage();
    this.updateUI();
    this.clearState();
  }

  /* ========================================
     MOBILE OPTIMIZATIONS
     ======================================== */

  initMobileOptimizations() {
    if (this.isMobileDevice()) {
      this.lastKnownWidth = window.innerWidth;
      this.lastKnownHeight = window.innerHeight;
      
      const inputs = this.container.querySelectorAll('input, select, textarea');
      inputs.forEach(input => {
        this.addEventListener(input, 'focus', () => {
          this.isInputFocused = true;
        });
        this.addEventListener(input, 'blur', () => {
          setTimeout(() => {
            this.isInputFocused = false;
          }, 300);
        });
      });
      
      this.addEventListener(window, 'orientationchange', () => {
        setTimeout(() => {
          if (this.canvas && this.image) {
            this.preserveCanvasState(() => {
              this.setupCanvasSize();
            });
          }
        }, 500);
      });
    }
  }

  preserveCanvasState(callback) {
    if (!this.canvas || !this.image) {
      callback();
      return;
    }
    
    const savedState = {
      zoomFactor: this.zoomFactor,
      cropX: this.cropX,
      cropY: this.cropY,
      cropWidth: this.cropWidth,
      cropHeight: this.cropHeight,
      imageX: this.imageX,
      imageY: this.imageY,
      imageWidth: this.imageWidth,
      imageHeight: this.imageHeight
    };
    
    callback();
    
    setTimeout(() => {
      if (this.image && this.canvas) {
        Object.assign(this, savedState);
        this.drawImage();
      }
    }, 50);
  }

  initKeyboardNavigation() {
    this.addEventListener(this.container, 'keydown', (e) => {
      if (e.key === 'Enter' && e.target.classList.contains('qty-input')) {
        e.preventDefault();
        
        if (!this.validateQuantityInput(e.target)) {
          return;
        }
        
        const currentIndex = this.allQuantityInputs.indexOf(e.target);
        if (currentIndex === -1 || this.allQuantityInputs.length <= 1) return;
        
        const offset = e.shiftKey ? -1 : 1;
        const nextIndex = (currentIndex + offset + this.allQuantityInputs.length) % this.allQuantityInputs.length;
        
        const nextInput = this.allQuantityInputs[nextIndex];
        if (nextInput && !nextInput.disabled) {
          nextInput.focus();
          nextInput.select();
        }
      }
      
      if (e.key === 'Escape' && e.target.classList.contains('qty-input')) {
        e.target.value = 0;
        e.target.blur();
        const variantId = e.target.dataset.variant;
        this.syncQuantityInputs(variantId, 0);
        this.debouncedUpdateVariant(variantId, 0);
      }
    });
  }

  /* ========================================
     UTILITY FUNCTIONS
     ======================================== */

  formatMoney(amount) {
    const currency = this.settings.currency || 'USD';
    
    try {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currency,
        minimumFractionDigits: 2
      }).format(amount);
    } catch (error) {
      return `$${amount.toFixed(2)}`;
    }
  }

  debounce(func, wait, immediate = false) {
    let timeout;
    const debounced = function executedFunction(...args) {
      const later = () => {
        timeout = null;
        if (!immediate) func(...args);
      };
      const callNow = immediate && !timeout;
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
      if (callNow) func(...args);
    };
    
    debounced.cancel = function() {
      clearTimeout(timeout);
      timeout = null;
    };
    
    return debounced;
  }

  throttle(func, limit) {
    let inThrottle;
    const throttled = function(...args) {
      if (!inThrottle) {
        func.apply(this, args);
        inThrottle = true;
        setTimeout(() => inThrottle = false, limit);
      }
    };
    
    throttled.cancel = function() {
      inThrottle = false;
    };
    
    return throttled;
  }

  measurePerformance(name, fn) {
    const startTime = performance.now();
    const result = fn();
    const endTime = performance.now();
    const duration = endTime - startTime;
    
    this.performanceMonitor.measure(name, duration);
    
    return result;
  }

  async measureAsyncPerformance(name, fn) {
    const startTime = performance.now();
    const result = await fn();
    const endTime = performance.now();
    const duration = endTime - startTime;
    
    this.performanceMonitor.measure(name, duration);
    
    return result;
  }

  addEventListener(element, event, handler, options = {}) {
    element.addEventListener(event, handler, options);
    this.eventListeners.set(element, { event, handler, options });
  }

  /* ========================================
     PROGRESS INDICATORS
     ======================================== */

  showProcessingIndicator(message = 'Processing...') {
    const progressEl = this.getOrCreateProgressElement('processing');
    progressEl.innerHTML = `
      <div class="progress-content">
        <div class="loading-spinner" aria-hidden="true"></div>
        <div class="progress-text">${message}</div>
      </div>
    `;
    progressEl.style.display = 'flex';
  }

  hideProcessingIndicator() {
    const progressEl = document.querySelector('.processing-progress');
    if (progressEl) {
      progressEl.style.display = 'none';
    }
  }

  showFileProcessingProgress() {
    const progressEl = this.getOrCreateProgressElement('file-processing');
    progressEl.innerHTML = `
      <div class="progress-content">
        <div class="loading-spinner" aria-hidden="true"></div>
        <div class="progress-text">Processing image...</div>
      </div>
    `;
    progressEl.style.display = 'flex';
  }

  hideFileProcessingProgress() {
    const progressEl = document.querySelector('.file-processing-progress');
    if (progressEl) {
      progressEl.style.display = 'none';
    }
  }

  showUploadProgress(percent, status = 'Processing...') {
    const progressEl = this.getOrCreateProgressElement('upload');
    
    const progressFill = progressEl.querySelector('.progress-fill');
    const progressText = progressEl.querySelector('.progress-text');
    
    if (progressFill) {
      progressFill.style.width = `${Math.min(100, Math.max(0, percent))}%`;
    }
    
    if (progressText) {
      progressText.textContent = `${status} ${Math.round(percent)}%`;
    }
    
    progressEl.style.display = 'flex';
    
    this.updateLiveRegions(`Upload ${Math.round(percent)}% complete`);
  }

  hideUploadProgress() {
    const progressEl = document.querySelector('.upload-progress');
    if (progressEl) {
      progressEl.style.display = 'none';
    }
  }

  getOrCreateProgressElement(type) {
    const className = `${type}-progress`;
    let progressEl = document.querySelector(`.${className}`);
    
    if (!progressEl) {
      progressEl = document.createElement('div');
      progressEl.className = className + ' upload-progress';
      progressEl.innerHTML = `
        <div class="progress-content">
          <div class="progress-bar">
            <div class="progress-fill"></div>
          </div>
          <div class="progress-text">Processing...</div>
        </div>
      `;
      document.body.appendChild(progressEl);
    }
    
    return progressEl;
  }

  showLoading() {
    const overlay = this.container.querySelector('.loading-overlay');
    if (overlay) {
      overlay.style.display = 'flex';
    }
    
    const button = this.container.querySelector('.add-to-cart-btn');
    if (button) {
      button.disabled = true;
      button.textContent = 'Processing...';
    }
  }

  hideLoading() {
    const overlay = this.container.querySelector('.loading-overlay');
    if (overlay) {
      overlay.style.display = 'none';
    }
    
    const button = this.container.querySelector('.add-to-cart-btn');
    if (button) {
      this.updateAddToCartButton();
    }
  }

  toggleRowLoading(variantId, isLoading) {
    const row = this.container.querySelector(`[data-variant-id="${variantId}"]`);
    if (!row) return;
    
    let spinner = row.querySelector('.row-loading-spinner');
    const quantitySelector = row.querySelector('.quantity-selector');
    const variantTotal = row.querySelector('.variant-total');
    
    if (isLoading) {
      this.processingQueue.add(variantId);
      
      if (!spinner) {
        spinner = this.createRowSpinner();
        row.appendChild(spinner);
      }
      
      spinner.style.display = 'flex';
      if (quantitySelector) {
        quantitySelector.style.opacity = '0.6';
        quantitySelector.style.pointerEvents = 'none';
      }
      if (variantTotal) variantTotal.style.opacity = '0.6';
      
    } else {
      this.processingQueue.delete(variantId);
      
      if (spinner) spinner.style.display = 'none';
      if (quantitySelector) {
        quantitySelector.style.opacity = '1';
        quantitySelector.style.pointerEvents = 'auto';
      }
      if (variantTotal) variantTotal.style.opacity = '1';
    }
  }

  createRowSpinner() {
    const spinner = document.createElement('div');
    spinner.className = 'row-loading-spinner';
    spinner.setAttribute('aria-hidden', 'true');
    return spinner;
  }

  showMessage(message, type = 'success', duration = 4000) {
    const existing = document.querySelector('.engraving-message');
    if (existing) existing.remove();
    
    const messageEl = document.createElement('div');
    messageEl.className = `engraving-message engraving-message--${type}`;
    messageEl.innerHTML = `
      <div class="message-content">
        <span class="message-icon">${this.getMessageIcon(type)}</span>
        <span class="message-text">${message}</span>
        <button class="message-close" aria-label="Close message">×</button>
      </div>
    `;
    
    this.addEventListener(messageEl.querySelector('.message-close'), 'click', () => {
      messageEl.remove();
    });
    
    document.body.appendChild(messageEl);
    
    if (duration > 0) {
      setTimeout(() => {
        if (messageEl.parentNode) {
          messageEl.remove();
        }
      }, duration);
    }
    
    return messageEl;
  }

  getMessageIcon(type) {
    const icons = {
      success: '✓',
      error: '⚠',
      warning: '⚠',
      info: 'ℹ'
    };
    return icons[type] || icons.info;
  }

  /* ========================================
     STATE PERSISTENCE & CLEANUP
     ======================================== */

  setupAutoSave() {
    this.autoSaveInterval = setInterval(() => {
      this.saveState();
    }, 30000);
  }

  saveState() {
    if (!window.localStorage) return;
    
    try {
      const state = {
        selectedVariants: Array.from(this.selectedVariants.entries()),
        hasUploadedFile: !!this.uploadedFile,
        isGrayscale: this.isGrayscale,
        cropShape: this.settings.cropShape,
        timestamp: Date.now()
      };
      
      const stateKey = `engraving_state_${this.productId}_${this.uniqueId}`;
      localStorage.setItem(stateKey, JSON.stringify(state));
    } catch (error) {
      // Silent fail for localStorage errors
    }
  }

  loadState() {
    if (!window.localStorage) return;
    
    try {
      const stateKey = `engraving_state_${this.productId}_${this.uniqueId}`;
      const stateData = localStorage.getItem(stateKey);
      
      if (stateData) {
        const state = JSON.parse(stateData);
        
        if (Date.now() - state.timestamp < 3600000) {
          state.selectedVariants.forEach(([variantId, quantity]) => {
            this.selectedVariants.set(variantId, quantity);
            this.syncQuantityInputs(variantId, quantity);
          });
          
          this.isGrayscale = state.isGrayscale || false;
          
          this.updateUI();
          
          if (state.selectedVariants.length > 0) {
            this.showMessage('ℹ Previous selections restored', 'info', 2000);
          }
        }
      }
    } catch (error) {
      // Silent fail for state loading errors
    }
  }

  clearState() {
    if (window.localStorage) {
      const stateKey = `engraving_state_${this.productId}_${this.uniqueId}`;
      localStorage.removeItem(stateKey);
    }
  }

  /* ========================================
     ERROR HANDLING & CLEANUP
     ======================================== */

  createErrorBoundary() {
    const errorBoundary = this.container.querySelector('.engraving-error-boundary');
    if (errorBoundary) {
      const refreshBtn = errorBoundary.querySelector('button');
      if (refreshBtn) {
        this.addEventListener(refreshBtn, 'click', () => {
          this.recoverFromError();
        });
      }
    }
  }

  handleCriticalError(context, error) {
    this.hasError = true;
    
    console.error(`Critical error in ${context}:`, error);
    
    const errorBoundary = this.container.querySelector('.engraving-error-boundary');
    if (errorBoundary) {
      errorBoundary.style.display = 'block';
    }
    
    const pageWidth = this.container.querySelector('.page-width');
    if (pageWidth) {
      pageWidth.style.display = 'none';
    }
    
    this.performanceMonitor.error('critical_error', { context, error: error.message });
  }

  recoverFromError() {
    if (this.retryCount >= this.maxRetries) {
      this.showMessage('⚠ Maximum retry attempts reached. Please refresh the page.', 'error');
      return;
    }
    
    this.retryCount++;
    this.hasError = false;
    
    const errorBoundary = this.container.querySelector('.engraving-error-boundary');
    if (errorBoundary) {
      errorBoundary.style.display = 'none';
    }
    
    const pageWidth = this.container.querySelector('.page-width');
    if (pageWidth) {
      pageWidth.style.display = 'block';
    }
    
    this.safeInit();
  }

  safeExecute(fn, context = 'Unknown operation') {
    if (this.hasError) return null;
    
    try {
      return fn();
    } catch (error) {
      console.error(`Error in ${context}:`, error);
      this.showMessage(`⚠ ${context} failed. Please try again.`, 'error');
      this.performanceMonitor.error('safe_execute_error', { context, error: error.message });
      return null;
    }
  }

  async safeAsync(fn, context = 'Async operation') {
    if (this.hasError) return null;
    
    try {
      return await fn();
    } catch (error) {
      console.error(`Async error in ${context}:`, error);
      throw new Error(`${context} failed: ${error.message}`);
    }
  }

  handleResize() {
    if (this.isMobileDevice()) {
      const currentWidth = window.innerWidth;
      const currentHeight = window.innerHeight;
      
      if (this.lastKnownWidth && this.lastKnownHeight) {
        const widthDiff = Math.abs(currentWidth - this.lastKnownWidth);
        const heightDiff = Math.abs(currentHeight - this.lastKnownHeight);
        
        if (widthDiff < 50 && heightDiff < 200) {
          return;
        }
      }
      
      this.lastKnownWidth = currentWidth;
      this.lastKnownHeight = currentHeight;
    }
    
    if (this.canvas && this.image && !this.isResizing) {
      this.preserveCanvasState(() => {
        this.setupCanvasSize();
      });
    }
  }

  handleVisibilityChange() {
    if (document.hidden) {
      this.pauseOperations();
    } else {
      this.resumeOperations();
    }
  }

  pauseOperations() {
    this.operationsPaused = true;
  }

  resumeOperations() {
    this.operationsPaused = false;
  }

  createLiveRegion() {
    if (!document.getElementById('engraving-live-region')) {
      const liveRegion = document.createElement('div');
      liveRegion.id = 'engraving-live-region';
      liveRegion.setAttribute('aria-live', 'polite');
      liveRegion.setAttribute('aria-atomic', 'true');
      liveRegion.className = 'sr-only';
      document.body.appendChild(liveRegion);
    }
  }

  updateLiveRegions(message) {
    const liveRegion = document.getElementById('engraving-live-region');
    if (liveRegion) {
      liveRegion.textContent = message;
      setTimeout(() => {
        liveRegion.textContent = '';
      }, 1000);
    }
  }

  setupIntersectionObserver() {
    if (!this.features.intersectionObserver) return;
    
    this.intersectionObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          this.handleIntersection(entry.target);
        }
      });
    }, {
      rootMargin: '50px',
      threshold: 0.1
    });
    
    this.intersectionObserver.observe(this.container);
  }

  handleIntersection(target) {
    if (!this.isLazyLoaded) {
      this.initializeLazyFeatures();
      this.isLazyLoaded = true;
    }
  }

  initializeLazyFeatures() {
    this.preloadCriticalAssets();
    this.initializeAdvancedFeatures();
  }

  preloadCriticalAssets() {
    if (this.features.requestIdleCallback) {
      requestIdleCallback(() => {
        this.preloadImageProcessingAssets();
      });
    } else {
      setTimeout(() => {
        this.preloadImageProcessingAssets();
      }, 100);
    }
  }

  preloadImageProcessingAssets() {
    const warmupCanvas = document.createElement('canvas');
    warmupCanvas.width = 1;
    warmupCanvas.height = 1;
    const warmupCtx = warmupCanvas.getContext('2d');
    warmupCtx.fillRect(0, 0, 1, 1);
  }

  initializeProgressTracking() {
    this.progressState = {
      fileUploaded: false,
      variantsSelected: false,
      readyToAddToCart: false
    };
  }

  initializeAdvancedFeatures() {
    if (this.features.webgl) {
      this.initWebGLProcessing();
    }
    
    this.initAdvancedImageFilters();
  }

  initWebGLProcessing() {
    // WebGL processing placeholder for future enhancements
  }

  initAdvancedImageFilters() {
    this.availableFilters = ['grayscale', 'sepia', 'contrast', 'brightness'];
  }

  destroy() {
    this.performanceMonitor.mark('module_cleanup_start');
    
    this.eventListeners.forEach((listener, element) => {
      element.removeEventListener(listener.event, listener.handler, listener.options);
    });
    this.eventListeners.clear();
    
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
      this.intersectionObserver = null;
    }
    
    if (this.canvas) {
      this.ctx = null;
      this.canvas = null;
    }
    if (this.image) {
      this.image = null;
    }
    
    this.processingQueue.clear();
    
    if (this.autoSaveInterval) {
      clearInterval(this.autoSaveInterval);
    }
    
    if (this.debouncedUpdateVariant?.cancel) {
      this.debouncedUpdateVariant.cancel();
    }
    if (this.throttledResize?.cancel) {
      this.throttledResize.cancel();
    }
    
    this.selectedVariants.clear();
    this.uploadedFile = null;
    this.croppedImageData = null;
    
    this.uploadAttempts.clear();
    
    this.performanceMonitor.mark('module_cleanup_end');
    this.performanceMonitor.measure('cleanup_duration', 'module_cleanup_start', 'module_cleanup_end');
  }
}

/* ========================================
   MODULE INITIALIZATION
   ======================================== */

const initializeEngravingModules = () => {
  const modules = document.querySelectorAll('.engraving-product-module');
  engravingLogger.dev(`🎨 Found ${modules.length} engraving modules to initialize`);
  
  if ('IntersectionObserver' in window) {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const module = entry.target;
          if (!module.classList.contains('loaded')) {
            try {
              new EngravingProductModule(module);
              engravingLogger.dev(`✅ Engraving module initialized: ${module.dataset.sectionId}`);
            } catch (error) {
              console.error(`❌ Failed to initialize engraving module:`, error);
            }
          }
          observer.unobserve(module);
        }
      });
    }, { rootMargin: '50px' });
    
    modules.forEach(module => observer.observe(module));
  } else {
    modules.forEach(module => {
      try {
        new EngravingProductModule(module);
        engravingLogger.dev(`✅ Engraving module initialized: ${module.dataset.sectionId}`);
      } catch (error) {
        console.error(`❌ Failed to initialize engraving module:`, error);
      }
    });
  }
};

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeEngravingModules);
} else {
  initializeEngravingModules();
}

// Global error handling
window.addEventListener('unhandledrejection', (event) => {
  if (event.reason && event.reason.message && event.reason.message.includes('engraving')) {
    console.error('Unhandled engraving module error:', event.reason);
    event.preventDefault();
  }
});

// Export for global access
window.EngravingProductModule = EngravingProductModule;

engravingLogger.dev('🎨 Enhanced Engraving Product Module v2.1 Loaded - Clean Circular Cropping');